/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import { Plugin, ButtonView, ViewNode } from 'ckeditor5';
import bulbImageIcon from '../../assets/block_toolbar_icons/image-regular.svg';
import { assetInsertLocation, cancelBusyIndicator } from '../utils';

export default class BulbImageUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName(): string {
    return 'BulbImageUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;

    editor.ui.componentFactory.add('bulbInsertImage', (locale) => {
      // The state of the button will be bound to the widget command.
      const command = editor.commands.get('insertBulbImage');

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('content_block_type_IMAGE'),
        icon: bulbImageIcon,
        tooltip: true,
        withText: true,
      });

      // Callback executed once the button is clicked:
      buttonView.on('execute', this.openAssetLibrarySelectionModal);

      // Listen for the event that is going to be fired from our client that the upload was successful.
      window.addEventListener('assetLibraryImageResponse', (event: any) => {
        assetInsertLocation(this.editor);
        editor.execute('insertBulbImage', event.detail.contentBlock, event.detail.nextIndex);
        const root = editor.model.document.getRoot();
        const uploadPlaceholder = root && Array.from(root.getChildren())
          .find((item) => item.is('element', 'bulbUploadPlaceholder')
            && item.getAttribute('asset-id') === event.detail.trackingId);
        if (uploadPlaceholder) {
          editor.model.change((writer) => writer.remove(uploadPlaceholder));
        }
        cancelBusyIndicator(editor);
      });

      // Listener to cancel busy on error.
      window.addEventListener('assetLibraryImageErrorResponse', () => {
        cancelBusyIndicator(editor);
      });

      // Bind the state of the button to the command.

      buttonView.bind('isOn', 'isEnabled').to(command as any, 'value', 'isEnabled');

      this.setupClipboardIntegration();

      return buttonView;
    });
  }

  /**
   * Method reference on our image button to execute an event to open up our assetlibrary selection modal.
   *
   * @param event
   * @private
   */
  private openAssetLibrarySelectionModal(event) {
    const openAssetLibraryEvent = new CustomEvent('openAssetLibraryImageType', {
      detail: {
        event,
        eventFiredFromEditor: true,
      },
    });
    window.dispatchEvent(openAssetLibraryEvent);
  }

  /**
   * Initialize paste image event and process image.
   * @private
   */
  private setupClipboardIntegration() {
    const { editor } = this;
    this.listenTo(editor.plugins.get('ClipboardPipeline') as any, 'inputTransformation', (evt, data) => {
      if (!((data.method === 'drop' && data.dataTransfer.files.length !== 0) || data.method === 'paste')) {
        return;
      }

      const dataController = editor.data;
      const modelFragment = dataController.toModel(data.content, '$clipboardHolder');

      const modelFragmentChildren = Array.from(modelFragment.getChildren());
      // @ts-ignore
      if (modelFragmentChildren.length > 0 && !['figure', 'image'].includes(modelFragmentChildren[0].name)) {
        return;
      }
      const docFragmentChildren = Array.from(data.content.getChildren());
      let elementOrFile;
      if (data.dataTransfer && data.dataTransfer.files && data.dataTransfer.files.length > 0) {
        elementOrFile = data.dataTransfer.files[0] as File;
        if (elementOrFile.type.includes('image')) {
          CKEBulbData.service.addParagraphBeforeDroppedBlock(editor, data);
          CKEBulbData.service.uploadAsset.uploadFileWithoutBrowser(elementOrFile, 'image/*', false, 'paste');
          evt.stop();
        }
      } else if (docFragmentChildren && docFragmentChildren.length > 0) {
        elementOrFile = docFragmentChildren[0] as ViewNode;
        if (elementOrFile && elementOrFile.name === 'figure') {
          if (elementOrFile._classes.has('image') && elementOrFile.getAttribute('src')) {
            CKEBulbData.service.uploadAsset.uploadFileWithUrl(
              elementOrFile.getAttribute('src'),
              'image/*',
              false,
              'paste',
            );
          }
          evt.stop();
        }
      }
    }, { priority: 'highest' });
  }
}
